export const getTargetName = (report: any) => {
	const target = report?.target || {}
	const reportType = `${report?.report_type || ''}`.toUpperCase()

	if (reportType === 'COMMENT' && target?.content) {
		return target.content
	}

	return (
		target?.name ||
		target?.business_full_name ||
		target?.item_name ||
		target?.title ||
		target?.author ||
		'-'
	)
}

const getPostTargetScope = (post: any) => {
	switch (`${post?.type || ''}`.toUpperCase()) {
		case 'EVENT':
			return 'EVENT'
		case 'HANGOUT':
			return 'HANGOUT'
		case 'ANNOUNCEMENT':
			return 'ANNOUNCEMENT'
		case 'DISCUSS_IN_CLUB':
			return 'COMMUNITY'
		case 'DISCUSS_IN_TOPIC':
			return 'DISCUSSION'
		case 'DISCUSS_IN_CHATROOM':
			return 'CHAT'
		case 'COURSE':
		case 'COURSE_UNIVINI':
		case 'COURSE_OF_LEANER':
		case 'COURSE_OF_TUTOR':
			return 'COURSE'
		default:
			return null
	}
}

const getConversationTargetScope = (conversation: any) => {
	switch (`${conversation?.kind || ''}`.toUpperCase()) {
		case 'CLUB':
			return 'COMMUNITY'
		case 'FMG':
			return 'SERVICE'
		case 'COURSE_AI':
		case 'COURSE_AI_SUPPORT':
			return 'COURSE_AI'
		case 'HANGOUT':
			return 'HANGOUT'
		case 'LOOKING_FOR':
			return 'LOOKING_FOR'
		case 'LMB':
			return 'LMB'
		case 'TALKROOM':
			return 'TALKROOM'
		case 'DATING':
			return 'DATING'
		case 'TUTOR':
			return 'COURSE'
		case 'PERSONAL':
		case 'MINI_CHAT':
		case 'CHAT_ROOM':
		case 'CHAT_LOCATION':
		case 'INBOX_GROUP':
			return 'CHAT'
		default:
			return null
	}
}

const getScopeSnapshot = (report: any) => {
	const scope = `${report?.last_action?.target_scope || ''}`
		.trim()
		.toUpperCase()
	return scope && scope !== 'UNKNOWN' ? scope : null
}

export const getReportTargetScope = (report: any) => {
	if (typeof report?.target_scope === 'string' && report.target_scope.trim()) {
		const backendScope = report.target_scope.toUpperCase()
		if (backendScope === 'UNKNOWN') return 'REMOVED_TARGET'
		// Older report-action snapshots used MARKETPLACE before the canonical LMB label.
		return backendScope === 'MARKETPLACE' ? 'LMB' : backendScope
	}

	const reportType = `${report?.report_type || ''}`.toUpperCase()
	const typeApp = `${report?.type_app || ''}`.toUpperCase()
	const target = report?.target || {}

	if (typeApp === 'COURSE_AI' && ['COURSE', 'COURSE_AI'].includes(reportType)) {
		return 'COURSE_AI'
	}
	if (reportType === 'COURSE_AI') return 'COURSE_AI'
	if (['BOOK', 'BOOK_PREVIEW'].includes(reportType) || typeApp === 'LIB')
		return 'LIBRARY'
	if (typeApp === 'LMB' || reportType.startsWith('LMB_')) return 'LMB'
	if (['POST', 'COURSE'].includes(reportType) && typeApp === 'TUTOR')
		return 'TUTOR'

	if (reportType === 'COMMENT') {
		if (target?.fmg_service_id) return 'SERVICE'
		return (
			getPostTargetScope(target?.post) ||
			getScopeSnapshot(report) ||
			'REMOVED_TARGET'
		)
	}

	if (['POST', 'EVENT', 'COURSE', 'HANGOUT'].includes(reportType)) {
		return (
			getPostTargetScope(target) ||
			(reportType === 'POST'
				? getScopeSnapshot(report) || 'REMOVED_TARGET'
				: reportType)
		)
	}

	if (reportType === 'USER') {
		if (typeApp === 'TUTOR') return 'TUTOR'
		return `${target?.type_app || ''}`.toUpperCase() === 'FMG'
			? 'SERVICE'
			: 'USER'
	}

	if (['CONVERSATION', 'CHAT_ROOM'].includes(reportType)) {
		return (
			getConversationTargetScope(target) ||
			(reportType === 'CHAT_ROOM' ? 'CHAT' : null) ||
			getScopeSnapshot(report) ||
			'REMOVED_TARGET'
		)
	}

	if (reportType === 'MESSAGE') {
		return (
			getPostTargetScope(target?.post) ||
			(`${target?.entity_type || ''}`.toUpperCase() === 'TALKROOM'
				? 'TALKROOM'
				: null) ||
			(target?.dating_id ||
			`${target?.entity_type || ''}`.toUpperCase() === 'DATING'
				? 'DATING'
				: null) ||
			getConversationTargetScope(target?.conversation) ||
			getConversationTargetScope(target?.related_conversation) ||
			getScopeSnapshot(report) ||
			'REMOVED_TARGET'
		)
	}
	if (reportType === 'TALKROOM') return 'TALKROOM'
	if (reportType === 'TRAIL') return 'TRAIL'
	if (reportType === 'DATING') return 'DATING'
	return getScopeSnapshot(report) || 'REMOVED_TARGET'
}

const TARGET_SCOPE_LABELS: Record<string, string> = {
	EVENT: 'Event',
	COMMUNITY: 'Community',
	DISCUSSION: 'Discussion',
	COURSE: 'Course',
	HANGOUT: 'Hangout',
	LOOKING_FOR: 'Looking For',
	ANNOUNCEMENT: 'Announcement',
	SERVICE: 'Service',
	COURSE_AI: 'Course AI',
	LIBRARY: 'Library',
	LMB: 'LMB',
	CHAT: 'Chat',
	TUTOR: 'Tutor',
	USER: 'User',
	DATING: 'Dating',
	TALKROOM: 'Talkroom',
	TRAIL: 'Trail',
	REMOVED_TARGET: 'Removed target',
}

export const getReportTargetScopeLabel = (report: any) => {
	const scope = getReportTargetScope(report)
	return TARGET_SCOPE_LABELS[scope] || 'Removed target'
}

export const getReportContentTypeLabel = (report: any) => {
	const reportType = `${report?.report_type || ''}`.trim()
	if (!reportType) return '-'

	return reportType
		.toLowerCase()
		.split('_')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ')
}

export const canDeleteReportContent = (report: any) => {
	if (typeof report?.can_delete_content === 'boolean') {
		return report.can_delete_content
	}

	const reportType = `${report?.report_type || ''}`.toUpperCase()
	const typeApp = `${report?.type_app || ''}`.toUpperCase()

	if (typeApp === 'COURSE_AI' && ['COURSE', 'COURSE_AI'].includes(reportType)) {
		return true
	}

	// Keep the CMS action available while reports from an older backend do not
	// yet include can_delete_content. The backend remains authoritative whenever
	// it explicitly supplies that flag above.
	if (
		reportType === 'CONVERSATION' &&
		['COMMUNITY', 'HANGOUT', 'LOOKING_FOR'].includes(
			getReportTargetScope(report),
		)
	) {
		return true
	}

	return [
		'POST',
		'EVENT',
		'COURSE',
		'HANGOUT',
		'COMMENT',
		'MESSAGE',
		'BOOK',
		'BOOK_PREVIEW',
		'LMB_LISTING',
	].includes(reportType)
}

export const getReportContentDeletedAt = (report: any) => {
	if (report?.content_deleted_at) {
		return report.content_deleted_at
	}

	const lastAction = report?.last_action
	if (lastAction?.content_deleted_at) {
		return lastAction.content_deleted_at
	}

	return lastAction?.type === 'delete_content'
		? lastAction.created_at || null
		: null
}

export const isReportContentDeleted = (report: any) =>
	Boolean(report?.is_content_deleted || getReportContentDeletedAt(report))
